import { useState, type FormEvent } from "react";
import {
  parseDatum,
  alsEingabetext,
  NAMENSARTEN,
  NAMENSART_LABEL,
  NAMENSART_HINWEIS,
  type Namensart,
  type UnscharfesDatum,
} from "@stammbaum/shared";
import { DatumFeld } from "./DatumFeld";
import { OrtFeld } from "./OrtFeld";

export interface PersonWerte {
  vorname: string;
  famname: string;
  /** Der im Alltag verwendete Vorname, wenn es nicht der erste ist. */
  rufname: string;
  maedchenname: string;
  geschlecht: "m" | "w" | "d" | "unbekannt";
  geburtText: string;
  todText: string;
  /**
   * Ort des Geburtsereignisses - bei einer Klinikgeburt eben die Klinik.
   * Getippt wird der ganze Pfad mit Komma; gespeichert wird eine Kette von
   * Ortszeilen, angezeigt nur die unterste Ebene.
   */
  geburtsortName: string;
  /**
   * Die Id des zuletzt gespeicherten Ortes. Sie gewinnt beim Speichern,
   * solange der Text unverändert ist - sonst würde ein Ortsname, der selbst
   * ein Komma enthält („Zwettl, NÖ" aus dem Altbestand), beim nächsten
   * Speichern in zwei Ebenen zerlegt und die Person hinge an einer neuen,
   * leeren Zeile statt an der gepflegten. Tippt jemand im Feld, fällt die
   * Id weg und der Pfad entscheidet.
   */
  geburtsortId: number | null;
  /**
   * Der letzte bekannte Wohnort, eine einzelne Angabe. Für den Lebensweg
   * mit Zeiträumen gibt es Wohnsitz-Ereignisse, die mehrfach vorkommen
   * dürfen.
   */
  wohnortName: string;
  wohnortId: number | null;
  beruf: string;
  kurzinfo: string;
  notiz: string;
  /**
   * Abweichende Namen: Schreibvarianten aus Quellen, angenommene Namen.
   * NICHT der Rufname — der ist eine Spalte an der Person, weil er der
   * Name ist, unter dem jemand täglich lief.
   */
  namen: NameZeile[];
  /**
   * Waren die Namen beim Öffnen überhaupt geladen?
   *
   * Die API lässt sie stehen, wenn das Feld fehlt — so kann ein Aufruf,
   * der sie gar nicht kennt, sie nicht versehentlich löschen. Diese
   * Unterscheidung muss bis hierher durchgehalten werden: eine Maske, die
   * mit einer Trefferzeile ohne `namen` geöffnet wurde, darf beim Speichern
   * keine leere Liste schicken.
   */
  namenBekannt: boolean;
}

export interface NameZeile {
  vorname: string;
  famname: string;
  art: Namensart;
  /** Fundstelle in Worten: „Taufbuch Echsenbach 1834, fol. 22". */
  notiz: string;
}

const LEERE_NAMENSZEILE: NameZeile = {
  vorname: "",
  famname: "",
  art: "variante",
  notiz: "",
};

export const LEERE_PERSON: PersonWerte = {
  vorname: "",
  famname: "",
  rufname: "",
  maedchenname: "",
  geschlecht: "unbekannt",
  geburtText: "",
  todText: "",
  geburtsortName: "",
  geburtsortId: null,
  wohnortName: "",
  wohnortId: null,
  beruf: "",
  kurzinfo: "",
  notiz: "",
  namen: [],
  // Eine neue Person hat noch keine - das ist Wissen, keine Unkenntnis.
  namenBekannt: true,
};

/** Aus einem Datensatz der API die Formularwerte machen. */
export function werteAusPerson(p: any, namen?: any[]): PersonWerte {
  const datum = (praefix: "geburt" | "tod"): UnscharfesDatum | null =>
    p[`${praefix}Von`] || p[`${praefix}Text`]
      ? {
          von: p[`${praefix}Von`] ?? null,
          bis: p[`${praefix}Bis`] ?? null,
          genauigkeit: p[`${praefix}Genauigkeit`] ?? "jahr",
          text: p[`${praefix}Text`] ?? null,
        }
      : null;

  return {
    vorname: p.vorname ?? "",
    famname: p.famname ?? "",
    rufname: p.rufname ?? "",
    maedchenname: p.maedchenname ?? "",
    geschlecht: p.geschlecht ?? "unbekannt",
    geburtText: alsEingabetext(datum("geburt")),
    todText: alsEingabetext(datum("tod")),
    // Im Feld steht der ganze Pfad, damit man die Kette auch ändern kann.
    geburtsortName: p.geburtsortPfad ?? p.geburtsortName ?? "",
    geburtsortId: p.geburtsortId ?? null,
    wohnortName: p.wohnortPfad ?? p.wohnortName ?? "",
    wohnortId: p.wohnortId ?? null,
    beruf: p.beruf ?? "",
    kurzinfo: p.kurzinfo ?? "",
    notiz: p.notiz ?? "",
    namen: (namen ?? p.namen ?? []).map((n: any) => ({
      vorname: n.vorname ?? "",
      famname: n.famname ?? "",
      art: (n.art ?? "variante") as Namensart,
      notiz: n.notiz ?? "",
    })),
    namenBekannt: Array.isArray(namen ?? p.namen),
  };
}

/** Formularwerte in das umwandeln, was die API erwartet. */
export function werteFuerApi(w: PersonWerte) {
  const leerZuNull = (s: string) => (s.trim() ? s.trim() : null);
  return {
    vorname: w.vorname.trim(),
    famname: w.famname.trim(),
    rufname: leerZuNull(w.rufname),
    maedchenname: leerZuNull(w.maedchenname),
    geschlecht: w.geschlecht,
    geburt: w.geburtText.trim() ? parseDatum(w.geburtText) : null,
    tod: w.todText.trim() ? parseDatum(w.todText) : null,
    geburtsortName: leerZuNull(w.geburtsortName),
    geburtsortId: w.geburtsortId,
    wohnortName: leerZuNull(w.wohnortName),
    wohnortId: w.wohnortId,
    beruf: leerZuNull(w.beruf),
    kurzinfo: leerZuNull(w.kurzinfo),
    notiz: leerZuNull(w.notiz),
    // Weglassen statt leer schicken, wenn wir sie nie gesehen haben —
    // sonst löscht ein Speichern aus einer Maske ohne Namensliste alle
    // abweichenden Namen.
    ...(w.namenBekannt
      ? {
          namen: w.namen
            .filter((n) => n.vorname.trim() || n.famname.trim())
            .map((n) => ({
              vorname: leerZuNull(n.vorname),
              famname: leerZuNull(n.famname),
              art: n.art,
              notiz: leerZuNull(n.notiz),
            })),
        }
      : {}),
  };
}

const feld =
  "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none " +
  "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";
const beschriftung = "text-sm text-black/60 dark:text-white/60";
/** Schmaler als `feld` — die Namenszeilen stehen zu viert nebeneinander. */
const namensfeld =
  "rounded-lg border border-black/15 bg-white px-2 py-1 text-sm outline-none " +
  "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";

export function PersonFormular({
  start,
  knopftext,
  fehler,
  laeuft,
  onSpeichern,
  onAbbrechen,
}: {
  start: PersonWerte;
  knopftext: string;
  fehler?: string | null;
  laeuft?: boolean;
  onSpeichern: (werte: PersonWerte) => void;
  onAbbrechen: () => void;
}) {
  const [w, setW] = useState<PersonWerte>(start);
  const setze = (teil: Partial<PersonWerte>) => setW((alt) => ({ ...alt, ...teil }));

  function absenden(e: FormEvent) {
    e.preventDefault();
    onSpeichern(w);
  }

  return (
    <form onSubmit={absenden} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={beschriftung}>Vorname</span>
          <input
            autoFocus
            value={w.vorname}
            onChange={(e) => setze({ vorname: e.target.value })}
            className={feld}
          />
        </label>
        <label className="block">
          <span className={beschriftung}>Familienname</span>
          <input
            value={w.famname}
            onChange={(e) => setze({ famname: e.target.value })}
            className={feld}
          />
        </label>
        <label className="block">
          <span className={beschriftung}>Rufname</span>
          <input
            value={w.rufname}
            onChange={(e) => setze({ rufname: e.target.value })}
            placeholder="wenn im Alltag ein anderer Vorname gilt"
            className={feld}
          />
        </label>
        <label className="block">
          <span className={beschriftung}>Geburtsname</span>
          <input
            value={w.maedchenname}
            onChange={(e) => setze({ maedchenname: e.target.value })}
            placeholder="falls durch Heirat geändert"
            className={feld}
          />
        </label>
        <div className="sm:col-span-2">
          <span className={beschriftung}>Geschlecht</span>
          <div className="mt-2 flex gap-4 text-sm">
            {(
              [
                ["m", "männlich"],
                ["w", "weiblich"],
                ["d", "divers"],
                ["unbekannt", "unbekannt"],
              ] as const
            ).map(([v, t]) => (
              <label key={v} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="geschlecht"
                  checked={w.geschlecht === v}
                  onChange={() => setze({ geschlecht: v })}
                />
                {t}
              </label>
            ))}
          </div>
        </div>

        <DatumFeld
          beschriftung="Geboren"
          wert={w.geburtText}
          onChange={(t) => setze({ geburtText: t })}
        />
        <DatumFeld
          beschriftung="Gestorben"
          wert={w.todText}
          onChange={(t) => setze({ todText: t })}
        />

        {/*
          Zwei Felder, zwei eigene Vorschlagslisten: die Liste hängt am
          zuletzt Getippten, und eine gemeinsame zeigte im Wohnort die
          Vorschläge zum Geburtsort.
        */}
        <OrtFeld
          beschriftung="Geburtsort"
          id="person-geburtsort-vorschlaege"
          wert={w.geburtsortName}
          onChange={(t) => setze({ geburtsortName: t, geburtsortId: null })}
          platzhalter="Ort, oder mit Komma: Haimschlag 30, Echsenbach"
          klasseFeld={feld}
          klasseBeschriftung={beschriftung}
        />
        <OrtFeld
          beschriftung="Wohnort"
          id="person-wohnort-vorschlaege"
          wert={w.wohnortName}
          onChange={(t) => setze({ wohnortName: t, wohnortId: null })}
          platzhalter="der letzte bekannte, Ebenen mit Komma"
          klasseFeld={feld}
          klasseBeschriftung={beschriftung}
        />
        <label className="block">
          <span className={beschriftung}>Beruf</span>
          <input
            value={w.beruf}
            onChange={(e) => setze({ beruf: e.target.value })}
            className={feld}
          />
        </label>
      </div>

      <label className="block">
        <span className={beschriftung}>Kurzinfo</span>
        <input
          value={w.kurzinfo}
          onChange={(e) => setze({ kurzinfo: e.target.value })}
          placeholder="eine Zeile, erscheint unter dem Namen im Baum"
          className={feld}
        />
      </label>

      {w.namenBekannt && (
        <div>
          <span className={beschriftung}>Abweichende Namen</span>
          <p className="text-xs text-black/40 dark:text-white/40">
            Wie die Person in Quellen geschrieben steht — Mayr / Maier / Meier — oder wie
            sie später hieß. Die Suche findet sie auch darüber. Eine Hälfte darf leer
            bleiben, wenn sich nur die andere ändert.
          </p>

          <ul className="mt-2 space-y-1.5">
            {w.namen.map((n, i) => {
              const aendere = (teil: Partial<NameZeile>) =>
                setze({ namen: w.namen.map((x, j) => (j === i ? { ...x, ...teil } : x)) });

              return (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <input
                    value={n.vorname}
                    onChange={(e) => aendere({ vorname: e.target.value })}
                    maxLength={80}
                    placeholder="Vorname"
                    className={namensfeld + " w-32"}
                  />
                  <input
                    value={n.famname}
                    onChange={(e) => aendere({ famname: e.target.value })}
                    maxLength={80}
                    placeholder="Familienname"
                    className={namensfeld + " w-36"}
                  />
                  <select
                    value={n.art}
                    onChange={(e) => aendere({ art: e.target.value as Namensart })}
                    title={NAMENSART_HINWEIS[n.art]}
                    className={namensfeld}
                  >
                    {NAMENSARTEN.map((a) => (
                      <option key={a} value={a}>
                        {NAMENSART_LABEL[a]}
                      </option>
                    ))}
                  </select>
                  <input
                    value={n.notiz}
                    onChange={(e) => aendere({ notiz: e.target.value })}
                    maxLength={500}
                    placeholder="woher? z.B. Taufbuch Echsenbach 1834, fol. 22"
                    className={namensfeld + " min-w-40 flex-1"}
                  />
                  <button
                    type="button"
                    title="Namen entfernen"
                    onClick={() => setze({ namen: w.namen.filter((_, j) => j !== i) })}
                    className="text-black/30 hover:text-red-700 dark:text-white/30"
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>

          {w.namen.length < 20 && (
            <button
              type="button"
              onClick={() => setze({ namen: [...w.namen, LEERE_NAMENSZEILE] })}
              className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              + Name
            </button>
          )}
        </div>
      )}

      <label className="block">
        <span className={beschriftung}>Notizen</span>
        <textarea
          value={w.notiz}
          onChange={(e) => setze({ notiz: e.target.value })}
          rows={5}
          className={feld}
        />
      </label>

      {fehler && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={laeuft}
          className="rounded-lg bg-tinte px-4 py-2 text-white disabled:opacity-50
                     dark:bg-white dark:text-tinte"
        >
          {laeuft ? "…" : knopftext}
        </button>
        <button
          type="button"
          onClick={onAbbrechen}
          className="rounded-lg border border-black/15 px-4 py-2 dark:border-white/15"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}
